import { IconButton, Tooltip, Snackbar, Alert, Portal, Box } from '@mui/material';
import { useRef, useEffect, useState } from 'react';
import AudioPlayer from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import DescriptionIcon from '@mui/icons-material/Description';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import ImageIcon from '@mui/icons-material/Image';
import SaveAltTwoToneIcon from '@mui/icons-material/SaveAltTwoTone';
import DownloadingIcon from '@mui/icons-material/Downloading';
import PdfViewer from './Pdfviewer';
import ExcelViewer from './ExcelViewer';
import VideoViewer from './VideoViewer';
import ImageViewer from './ImageViewer';

import downloadHelper from '../../utils/downloadHelper';

const getExt = (str) => {
  const match = str && str.match(/\.([a-z0-9]+)(\?.*)?$/i);

  return match ? match[1] : '';
};

const FileItem = ({ file, playingId, setPlayingId, getIconOnly }) => {
  const lref = useRef();
  const audioRef = useRef();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });
  const [downloading, setDownloading] = useState(false);
  const [touchStarted, setTouchStarted] = useState(false);
  // Get filename and url
  const filename = (file.filename || file.file_name || '').toLowerCase();
  const fileurl = (file.surl || file.url || file.cdn_url || file.turl || '')?.toLowerCase();
  const ext = getExt(filename) || getExt(fileurl);

  // Audio file extensions
  const isAudio = ext.match(/(mp3|wav|ogg|aac)$/);

  // Unique id for this audio (use file.id or file.surl as fallback)
  const audioId = file.id || file.surl || file.url || file.cdn_url || file.turl;

  // Always call hooks at the top level!
  useEffect(() => {
    if (audioRef.current && playingId !== audioId) {
      audioRef.current.audio.current.pause();
    }
  }, [playingId, audioId]);

  // File icon or preview
  const getFileIcon = () => {
    if (file.mtyp === 'I' || ext.match(/(jpg|jpeg|png|gif|bmp|webp)$/)) {
      return <ImageIcon color="primary" />;
    }
    if (ext.match(/pdf$/)) return <PictureAsPdfIcon color="error" />;
    if (isAudio) return <AudiotrackIcon color="success" />;
    if (ext.match(/(mp4|avi|mov|wmv|flv|webm)$/)) return <VideoFileIcon color="secondary" />;
    if (ext.match(/(xls|xlsx|csv)$/)) return <TableChartIcon color="info" />;
    if (ext.match(/(doc|docx)$/)) return <DescriptionIcon color="primary" />;

    return <InsertDriveFileIcon />;

  };

  if (getIconOnly) {
    return getFileIcon();
  }

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleTouchStart = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setTouchStarted(true);
  };

  const handleTouchEnd = async (event, downloadFn) => {
    event.preventDefault();
    event.stopPropagation();
    if (touchStarted && !downloading) {
      await downloadFn();
    }
    setTouchStarted(false);
  };

  if (isAudio) {
    const src = file.cdn_url || file.surl || file.url || file.turl;

    const downloadFromCDN = async () => {
      setDownloading(true);
      const { message, status } = await downloadHelper(src, 'Audio');

      showSnackbar(message, status);
      setDownloading(false);
      setTouchStarted(false);
    };

    const DownloadBtn = (
      <Tooltip title="Download" arrow>
        <IconButton
          size="small"
          onClick={() => {
            // Only handle click if no touch events occurred
            setTimeout(() => {
              if (!touchStarted) {
                downloadFromCDN();
              }
            }, 50);
          }}
          onTouchStart={handleTouchStart}
          onTouchEnd={(e) => handleTouchEnd(e, downloadFromCDN)}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          disabled={downloading}
          sx={{
            fontSize: '0.70rem',
            borderRadius: 2,
            color: 'white',
            minHeight: 28,
            minWidth: 28,
            padding: '2px 8px',
            backgroundColor: '#4ea5f9',
            '&:hover': { backgroundColor: '#509af4' },
            display: 'flex',
            alignItems: 'center',
            touchAction: 'manipulation',
            WebkitTouchCallout: 'none',
            WebkitUserSelect: 'none',
            WebkitTapHighlightColor: 'transparent',
            userSelect: 'none',
          }}
        >
          <SaveAltTwoToneIcon fontSize="small" sx={{ marginRight: 0.3 }} />
          <span style={{ fontSize: '0.70rem' }}>Download</span>
        </IconButton>
      </Tooltip>
    );

    return (
      <>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            display: 'block',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              width: '100%',
            }}
          >
            <AudioPlayer
              ref={audioRef}
              src={src}
              volume={0.5}
              style={{
                borderRadius: 8,
                width: '100%',
                minWidth: 250,
                maxWidth: 500,
                margin: '0 auto',
              }}
              showJumpControls={true}
              customVolumeControls={['VOLUME']}
              onPlay={() => setPlayingId && setPlayingId(audioId)}
            />

            {DownloadBtn}
          </Box>

          {downloading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.7)',
                borderRadius: 2,
                zIndex: 10,
              }}
            >
              <DownloadingIcon
                sx={{
                  fontSize: 40,
                  color: 'primary.main',
                  animation: 'pulse 1s infinite',
                  '@keyframes pulse': {
                    '0%': { opacity: 0.3 },
                    '50%': { opacity: 1 },
                    '100%': { opacity: 0.3 },
                  },
                }}
              />
            </Box>
          )}
        </Box>

        <Portal>
          <Snackbar
            open={snackbar.open}
            autoHideDuration={3000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              variant="filled"
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Portal>
      </>
    );

  }

  const isPDF = ext.match(/pdf$/);

  if (isPDF) {
    const pdfUrl = file.cdn_url || file.surl || file.url || file.turl;

    if (!pdfUrl) {
      return (
        <span>
          <div style={{ marginTop: 8, color: 'red' }}>
            PDF file not available.
          </div>
        </span>
      );
    }

    return (
      <span>
        <div style={{ marginTop: 8 }}>
          <PdfViewer pdffile={{ surl: pdfUrl }} />
        </div>
      </span>
    );
  }

  const isExcel = ext.match(/(xls|xlsx|csv)$/);

  if (isExcel) {
    return (
      <span>
        <div style={{ marginTop: 8 }}>
          <ExcelViewer excelfile={file} />
        </div>
      </span>
    );
  }

  const isVideo = ext.match(/(mp4|avi|mov|wmv|flv|webm)$/);

  if (isVideo) {
    return (
      <span>
        <div style={{ marginTop: 8 }}>
          <VideoViewer videofile={file} />
        </div>
      </span>
    );
  }

  const isImage = file.mtyp === 'I' || ext.match(/(jpg|jpeg|png|gif|bmp|webp)$/);

  if (isImage) {
    return (
      <span>
        <div style={{ marginTop: 8 }}>
          <ImageViewer imagefile={file} />
        </div>
      </span>
    );
  }

  return (
    <span>
      <IconButton 
        onClick={() => {
          setTimeout(() => {
            if (!touchStarted) {
              lref.current.click();
            }
          }, 50);
        }}
        onTouchStart={handleTouchStart}
        onTouchEnd={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (touchStarted) {
            lref.current.click();
          }
          setTouchStarted(false);
        }}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        sx={{
          touchAction: 'manipulation',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          WebkitTapHighlightColor: 'transparent',
          userSelect: 'none',
        }}
      >
        {getFileIcon()}
        <a
          ref={lref}
          style={{ display: 'none' }}
          href={file.surl}
          target="_blank"
          rel="noreferrer"
          download={file.surl ? file.surl.split('/').pop() : ''}
        >
          File
        </a>
      </IconButton>
    </span>
  );
};

export default FileItem;
